import React, { useEffect, useState } from 'react';
import { List, Table, Button, Modal, Spin, notification, Typography } from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import axiosService from 'services/axios.service';
import { formatDateTime } from 'utils';

const { Text } = Typography;

export const SubscriptionReconciliationList: React.FC<IResourceComponentsProps> = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [syncModalVisible, setSyncModalVisible] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [selectedSub, setSelectedSub] = useState<any>(null);
    const [paypalData, setPaypalData] = useState<any>(null);

    const fetchDiscrepancies = async () => {
        setLoading(true);
        try {
            const response = await axiosService.get('/v1/admin/subscription-transactions/reconcile/discrepancies');
            setData(response.data.data);
        } catch (error: any) {
            notification.error({
                message: 'Error fetching discrepancies',
                description: error.message || 'Something went wrong',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async (paypalSubscriptionId: string) => {
        setSyncLoading(true);
        setPaypalData(null);
        setSelectedSub(paypalSubscriptionId);
        setSyncModalVisible(true);
        try {
            const response = await axiosService.get(`/v1/admin/subscription-transactions/reconcile/sync/${paypalSubscriptionId}`);
            setPaypalData(response.data.data);
        } catch (error: any) {
            notification.error({
                message: 'Error syncing with PayPal',
                description: error.message || 'Something went wrong',
            });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!id) {
            notification.warning({ message: 'Invalid Transaction ID' });
            return;
        }
        try {
            await axiosService.delete(`/v1/admin/subscription-transactions/${id}`);
            notification.success({ message: 'Transaction deleted successfully' });
            // Refresh local data after delete
            if (selectedSub) {
                const response = await axiosService.get(`/v1/admin/subscription-transactions/reconcile/sync/${selectedSub}`);
                setPaypalData(response.data.data);
            }
            fetchDiscrepancies();
        } catch (error: any) {
            notification.error({
                message: 'Error deleting transaction',
                description: error.message || 'Something went wrong',
            });
        }
    };

    useEffect(() => {
        fetchDiscrepancies();
    }, []);

    const columns = [
        {
            title: 'User',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Period',
            key: 'period',
            render: (_: any, record: any) => `${record._id.month}/${record._id.year}`,
        },
        {
            title: 'PayPal Sub ID',
            key: 'subId',
            render: (_: any, record: any) => {
                const subId = record.transactions.find((t: any) => t.paypalSubscriptionId && t.paypalSubscriptionId !== 'free')?.paypalSubscriptionId;
                return subId || <Text type="secondary">N/A</Text>;
            }
        },
        {
            title: 'Record Count',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => {
                const subId = record.transactions.find((t: any) => t.paypalSubscriptionId && t.paypalSubscriptionId !== 'free')?.paypalSubscriptionId;
                return (
                    <Button
                        type='primary'
                        onClick={() => handleSync(subId)}
                        disabled={!subId}
                        title={!subId ? "No PayPal Subscription ID found in these records" : ""}
                    >
                        Sync with PayPal
                    </Button>
                );
            },
        },
    ];

    return (
        <List title='Subscription Reconciliation'>
            <Table
                dataSource={data}
                columns={columns}
                loading={loading}
                rowKey={(record) => `${record._id.user}-${record._id.year}-${record._id.month}`}
            />

            <Modal
                title={`PayPal Comparison: ${selectedSub}`}
                visible={syncModalVisible}
                onCancel={() => setSyncModalVisible(false)}
                width={1000}
                footer={[
                    <Button key='back' onClick={() => setSyncModalVisible(false)}>
                        Close
                    </Button>,
                ]}
            >
                {syncLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size='large' />
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: '20px' }}>
                            <Text strong>DB Records:</Text>
                            <Table
                                dataSource={paypalData?.dbRecords || []}
                                pagination={false}
                                size='small'
                                columns={[
                                    { title: 'Trans ID', dataIndex: 'paypalPayeeTransactionId' },
                                    { title: 'Start', dataIndex: 'dateStart', render: (val) => formatDateTime(val) },
                                    { title: 'End', dataIndex: 'dateEnd', render: (val) => formatDateTime(val) },
                                    { title: 'Cost', dataIndex: 'cost' },
                                    {
                                        title: 'Action',
                                        render: (_: any, record: any) => (
                                            <Button danger size='small' onClick={() => handleDelete(record._id)}>
                                                Delete Duplicate
                                            </Button>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                        <div>
                            <Text strong>PayPal Official Transactions:</Text>
                            <Table
                                dataSource={paypalData?.paypalTransactions || []}
                                pagination={false}
                                size='small'
                                columns={[
                                    { title: 'ID', dataIndex: 'id' },
                                    { title: 'Time', dataIndex: 'time', render: (val) => formatDateTime(val) },
                                    { title: 'Status', dataIndex: 'status' },
                                    { title: 'Amount', dataIndex: 'amount_with_breakdown', render: (val) => val?.gross_amount?.value },
                                ]}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </List>
    );
};
